import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { toast } from "sonner";

import { Button } from "@/shared/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/shared/components/ui/dialog";
import { useApi } from "@/shared/utils/request";

function toApiDate(d: Date): string {
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
}

function invalidateEntries(queryClient: ReturnType<typeof useQueryClient>, sandboxProfId: string) {
	queryClient.invalidateQueries({ queryKey: ["internal", "entries", sandboxProfId], exact: false });
}

// ── Import dialog ──────────────────────────────────────────────────

interface ImportProps {
	sandboxProfId: string;
	realFullName: string;
	date: Date;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export function SandboxImportDialog({
	sandboxProfId,
	realFullName,
	date,
	open,
	onOpenChange,
}: ImportProps) {
	const { makeApiRequest } = useApi();
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async () => {
			const apiDate = toApiDate(date);
			const res = await makeApiRequest({
				endpoint: `/sandbox/${sandboxProfId}/import`,
				method: "POST",
				body: { from: apiDate, to: apiDate },
			});
			if (res.error) throw new Error(String(res.error));
			return res.data as { importedCount: number; replacedCount: number };
		},
		onSuccess: (data) => {
			invalidateEntries(queryClient, sandboxProfId);
			toast.success(
				data.replacedCount > 0
					? `Imported ${data.importedCount} entries (replaced ${data.replacedCount} existing)`
					: `Imported ${data.importedCount} entries`,
			);
			onOpenChange(false);
		},
		onError: (e) =>
			toast.error("Import failed", {
				description: e instanceof Error ? e.message : undefined,
			}),
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>Import {format(date, "PP")}</DialogTitle>
					<DialogDescription>
						Clone {realFullName}'s entries for {format(date, "PP")} into this sandbox. Any
						existing sandbox entries for that day will be replaced.
					</DialogDescription>
				</DialogHeader>

				<DialogFooter>
					<Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
						Cancel
					</Button>
					<Button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
						{mutation.isPending ? "Importing..." : "Import day"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

// ── Reset dialog ───────────────────────────────────────────────────

interface ResetProps {
	sandboxProfId: string;
	realFullName: string;
	date: Date;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export function SandboxResetDialog({
	sandboxProfId,
	realFullName,
	date,
	open,
	onOpenChange,
}: ResetProps) {
	const { makeApiRequest } = useApi();
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async () => {
			const res = await makeApiRequest({
				endpoint: `/sandbox/${sandboxProfId}/reset`,
				method: "POST",
				body: { date: toApiDate(date) },
			});
			if (res.error) throw new Error(String(res.error));
			return res.data as { importedCount: number; replacedCount: number };
		},
		onSuccess: (data) => {
			invalidateEntries(queryClient, sandboxProfId);
			toast.success(`Reset complete — ${data.importedCount} entries reloaded from ${realFullName}`);
			onOpenChange(false);
		},
		onError: (e) =>
			toast.error("Reset failed", {
				description: e instanceof Error ? e.message : undefined,
			}),
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>Reset {format(date, "PP")}</DialogTitle>
					<DialogDescription>
						Discard any sandbox edits for this day and re-clone {realFullName}'s entries
						as they are right now. Sandbox work for {format(date, "PP")} will be lost.
					</DialogDescription>
				</DialogHeader>

				<DialogFooter>
					<Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
						Cancel
					</Button>
					<Button
						type="button"
						variant="destructive"
						onClick={() => mutation.mutate()}
						disabled={mutation.isPending}
					>
						{mutation.isPending ? "Resetting..." : "Reset day"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
